import {
  CalibrationType,
  CarOwnerShip,
  country,
  Document,
  Dossier,
  DossierComments,
  DossierCommentsEnum,
  FitterVehicle,
  getLatestDossierCommentTextByType,
  getCoverMessage,
  Glass,
  isCovered,
  LocationDataGeocoded,
  OperationType,
  PartAvailabilityStatus,
  PhoneNumber,
  SinisterContext,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';

export class AgendaFormDossier {
  id?: string;
  customer: AgendaFormCustomer;
  claim: AgendaFormClaim;
  appointment?: AgendaFormAppointment;
  policyInformation?: AgendaFormPolicyInformation;
  dossierComments: DossierComments[];

  constructor(
    dossier: Dossier,
    featureFlipManager: FeatureFlippingManagerService,
    translator: TranslatorService
  ) {
    this.id = dossier.id ?? null;
    (this.dossierComments = dossier?.dossierComments || []),
      (this.customer = {
        firstname: dossier?.customer?.firstname ?? null,
        lastname: dossier?.customer?.lastname ?? null,
        email: dossier?.customer?.email ?? null,
        phoneNumber: {
          number: dossier?.customer?.phoneNumber?.number ?? null,
          countryCode: dossier?.customer?.phoneNumber?.countryCode ?? null,
          internationalCode:
            dossier?.customer?.phoneNumber?.internationalCode ?? null,
        },
        location: dossier?.customer?.location ?? null,
        billingLocation: dossier?.customer?.billingLocation ?? null,
      });
    this.claim = {
      car: {
        licensePlateNumber: dossier?.claim?.car?.licensePlateNumber ?? null,
        countryCode: dossier?.claim?.car?.countryCode ?? null,
        manufacturer: dossier?.claim?.car?.manufacturer ?? null,
        model: dossier?.claim?.car?.model ?? null,
        registrationCode: dossier?.claim?.car?.registrationCode ?? null,
        version: dossier?.claim?.car?.version ?? null,
        ownerShip: {
          type: dossier?.claim?.car?.ownerShip?.type ?? null,
          name: dossier?.claim?.car?.ownerShip?.name ?? null,
        },
        characteristics: dossier?.claim?.car?.characteristics ?? null,
        firstRegistration: dossier?.claim?.car?.firstRegistration ?? null,
        parts: dossier?.claim?.car?.parts ?? null,
        vehicleType: dossier?.claim?.car?.vehicleType ?? null,
        vinNumber: dossier?.claim?.car?.vinNumber ?? null,
      },
      glass: { glass: dossier?.claim?.glass?.glass ?? null },
      operationType: dossier?.claim?.operationType ?? null,
      sinisterContext: dossier?.claim?.sinisterContext ?? {
        key: null,
        value: null,
      },
      sinisterDate: dossier?.claim?.sinisterDate || null,
      sinisterLocation: dossier?.claim?.sinisterLocation ?? null,
      documents: dossier?.claim?.documents ?? [],
      calibration: dossier?.claim?.calibration ?? null,
      eurocode: dossier?.claim?.eurocode ?? null,
      constructorRef: dossier?.claim?.constructorRef ?? null,
    };
    this.appointment = {
      fitterId:
        dossier?.appointment?.fitterId ??
        dossier?.requestAppointment?.fitterId ??
        null,
      id: dossier?.appointment?.id ?? null,
      outsideIntervention:
        dossier?.appointment?.outsideIntervention ??
        dossier?.requestAppointment?.outsideIntervention ??
        null,
      time: dossier?.appointment?.time ?? null,
      endTime: dossier?.appointment?.endTime ?? null,
      isReplacementVehicleSelected:
        dossier?.appointment?.isReplacementVehicleSelected ??
        dossier?.requestAppointment?.isReplacementVehicleSelected ??
        null,
      assignedReplacementVehicle:
        dossier?.appointment?.assignedReplacementVehicle ??
        dossier?.requestAppointment?.assignedReplacementVehicle ??
        null,
      assignedReplacementVehicleUse:
        dossier?.appointment?.assignedReplacementVehicleUse ??
        dossier?.requestAppointment?.assignedReplacementVehicleUse ??
        null,
      timezone:
        dossier?.appointment?.timezone ??
        dossier?.requestAppointment?.timezone ??
        null,
      brandId:
        dossier?.appointment?.brandId ??
        dossier?.requestAppointment?.brandId ??
        null,
      assignedResources:
        dossier?.appointment?.assignedResources ??
        dossier?.requestAppointment?.assignedResources ??
        null,
      assignedInterventionVehicle:
        dossier?.appointment?.assignedInterventionVehicle ??
        dossier?.requestAppointment?.assignedInterventionVehicle ??
        null,
      interventionDuration: dossier?.appointment?.interventionDuration ?? null,
      interventionLocation:
        dossier?.appointment?.interventionLocation ??
        dossier?.requestAppointment?.interventionLocation ??
        null,
      isValidatedByFitter: dossier?.appointment?.isValidatedByFitter ?? false,
      partAvailability:
        dossier?.appointment?.partAvailability ??
        dossier?.requestAppointment?.partAvailability ??
        null,
      partProvider:
        dossier?.appointment?.partProvider ??
        dossier?.requestAppointment?.partProvider ??
        null,
      isConveying:
        dossier?.appointment?.isConveying ??
        dossier?.requestAppointment?.isConveying ??
        false,
      interventionId: dossier?.appointment?.interventionId ?? null,
    };

    const coverAllGlasses = featureFlipManager.isFeatureActivated(
      'display-insurance-cover-for-all-glass'
    );
    const coverMessage = getCoverMessage(
      dossier,
      { coverAllGlasses },
      'fitter'
    );
    this.policyInformation = {
      insuranceChoice:
        dossier?.policyInformation?.insuranceChoice ??
        (isCovered(dossier, { coverAllGlasses }) === true ? 'true' : null),
      insuranceCode: dossier?.policyInformation?.insuranceCode ?? null,
      insuranceName: getInsuranceName(
        dossier?.policyInformation?.insuranceName
      ),
      policyNumber: dossier?.policyInformation?.policyNumber ?? null,
      vatNumber: dossier?.policyInformation?.vatNumber ?? null,
      excess:
        translator.getInstantTranslation(
          coverMessage.key,
          coverMessage.params
        ) ?? null,
      sinisterNumber: dossier?.policyInformation?.sinisterNumber ?? null,
    };
  }
}

export interface AgendaFormCustomer {
  email?: string;
  phoneNumber?: PhoneNumber;
  firstname: string;
  lastname: string;
  location?: LocationDataGeocoded;
  billingLocation?: LocationDataGeocoded;
}

export interface AgendaFormClaim {
  car: AgendaFormCar;
  glass: AgendaFormSinisterGlass;
  sinisterDate?: Date;
  operationType?: OperationType;
  sinisterLocation?: string;
  sinisterContext?: SinisterContext;
  documents?: Document[];
  calibration?: CalibrationType;
  eurocode?: string;
  constructorRef?: string;
}

export interface AgendaFormCar {
  registrationCode: string;
  licensePlateNumber: string;
  countryCode: country;
  manufacturer: string;
  version: string;
  model: string;
  firstRegistration?: Date;
  ownerShip?: CarOwnerShip;
  characteristics: any;
  parts: any;
  vehicleType?: string;
  vinNumber?: string;
}

export interface AgendaFormSinisterGlass {
  glass: Glass;
}

export interface AgendaFormAppointment {
  id: string;
  fitterId: string;
  brandId?: string;
  time: string | Date;
  endTime?: string | Date;
  timezone: string;
  isReplacementVehicleSelected?: boolean;
  outsideIntervention: boolean;
  interventionLocation?: LocationDataGeocoded;
  interventionDuration?: number;
  isValidatedByFitter?: boolean;
  comment?: string;
  partAvailability?: PartAvailabilityStatus;
  assignedResources?: string[];
  assignedInterventionVehicle?: string;
  assignedReplacementVehicle?: FitterVehicle;
  assignedReplacementVehicleUse?: {
    start?: string;
    end?: string;
  };
  partProvider?: string;
  isConveying?: boolean;
  interventionId?: string;
}

export interface AgendaFormPolicyInformation {
  insuranceChoice: string;
  insuranceName: string;
  insuranceCode: string;
  policyNumber: string;
  vatNumber?: string;
  excess?: string;
  sinisterNumber?: string;
}

export type AgendaCreationDataOrigin =
  | 'AGENDA'
  | 'B2B'
  | 'B2C'
  | 'API'
  | 'PARTNERS_SYNC';

export function getInsuranceName(insuranceName: string) {
  return insuranceName === null ||
    insuranceName === undefined ||
    insuranceName === '' ||
    insuranceName === 'NONE'
    ? null
    : insuranceName;
}
